import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useFeatureFlags } from "@/context/FeatureFlagsContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Flag as FlagIcon, Plus, Loader2, Trash2, Save, ShieldCheck, ShieldAlert,
  Users, UserCheck, UserX, AlertTriangle, Search,
} from "lucide-react";
import { ROLE_LABELS } from "@/context/AuthContext";

const ALL_ROLES = ["super_admin", "capo_area", "resident_manager", "housekeeping_manager", "front_office_manager", "read_only"];

function StatusBadge({ flag }) {
  if (flag.enabled_globally) {
    return (
      <span data-testid={`flag-status-${flag.key}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300">
        <ShieldCheck className="h-3 w-3" /> Attivo globale
      </span>
    );
  }
  const someone =
    (flag.enabled_for_roles?.length || 0) > 0 ||
    (flag.enabled_for_users?.length || 0) > 0;
  if (someone) {
    return (
      <span data-testid={`flag-status-${flag.key}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
        <ShieldAlert className="h-3 w-3" /> Solo utenti selezionati
      </span>
    );
  }
  return (
    <span data-testid={`flag-status-${flag.key}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold bg-muted text-muted-foreground">
      Disattivo
    </span>
  );
}

function UserPicker({ testIdPrefix, label, icon: Icon, users, selected, onChange, colorClass }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users.slice(0, 8);
    return users
      .filter((u) =>
        (u.email || "").toLowerCase().includes(s) ||
        (u.nome || "").toLowerCase().includes(s) ||
        (u.cognome || "").toLowerCase().includes(s)
      )
      .slice(0, 10);
  }, [q, users]);
  const selectedObjects = users.filter((u) => selected.includes(u.id));
  const toggle = (id) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);

  return (
    <div className="space-y-1.5">
      <div className={`flex items-center gap-1.5 text-xs uppercase tracking-[0.16em] ${colorClass}`}>
        <Icon className="h-3.5 w-3.5" /> {label}
        <span className="text-muted-foreground normal-case tracking-normal">
          {selected.length > 0 ? `· ${selected.length} selezionati` : ""}
        </span>
      </div>
      {selectedObjects.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selectedObjects.map((u) => (
            <span
              key={u.id}
              data-testid={`${testIdPrefix}-chip-${u.id}`}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-lavender-100 dark:bg-lavender-900/40 text-lavender-900 dark:text-lavender-200"
            >
              {u.nome} {u.cognome}
              <button
                type="button"
                onClick={() => toggle(u.id)}
                className="ml-0.5 hover:text-rose-600"
                aria-label="Rimuovi"
              >×</button>
            </span>
          ))}
        </div>
      )}
      <div className="relative">
        <Search className="h-3.5 w-3.5 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input
          data-testid={`${testIdPrefix}-search`}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Cerca per nome o email"
          className="pl-8 bg-background/40 text-sm"
        />
      </div>
      {(q || filtered.length > 0) && (
        <div className="rounded-lg border border-border/60 bg-background/40 max-h-40 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="text-xs text-muted-foreground text-center py-2">Nessun utente</div>
          ) : filtered.map((u) => (
            <label
              key={u.id}
              className="flex items-center gap-2 px-2 py-1.5 hover:bg-lavender-50/50 dark:hover:bg-lavender-950/20 text-sm cursor-pointer"
            >
              <Checkbox
                data-testid={`${testIdPrefix}-user-${u.id}`}
                checked={selected.includes(u.id)}
                onCheckedChange={() => toggle(u.id)}
              />
              <span className="flex-1 truncate">{u.nome} {u.cognome}</span>
              <span className="text-xs text-muted-foreground truncate max-w-[180px]">{u.email}</span>
              <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{ROLE_LABELS[u.role] || u.role}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function FlagCard({ flag, users, onSaved, onDeleted }) {
  const [dirty, setDirty] = useState(false);
  const [label, setLabel] = useState(flag.label || "");
  const [desc, setDesc] = useState(flag.description || "");
  const [eg, setEg] = useState(!!flag.enabled_globally);
  const [roles, setRoles] = useState([...(flag.enabled_for_roles || [])]);
  const [enUsers, setEnUsers] = useState([...(flag.enabled_for_users || [])]);
  const [disUsers, setDisUsers] = useState([...(flag.disabled_for_users || [])]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelOpen, setConfirmDelOpen] = useState(false);
  const [confirmGlobalOffOpen, setConfirmGlobalOffOpen] = useState(false);

  const markDirty = () => setDirty(true);

  const toggleRole = (r) => {
    setRoles((prev) => prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]);
    markDirty();
  };

  const attemptToggleGlobal = (next) => {
    // Se sto per disattivare (era ON), warning se ci sono utenti attivi
    if (!next && flag.enabled_globally) {
      setConfirmGlobalOffOpen(true);
      return;
    }
    setEg(next); markDirty();
  };

  const doSave = async () => {
    setSaving(true);
    try {
      const payload = {
        label,
        description: desc,
        enabled_globally: eg,
        enabled_for_roles: roles,
        enabled_for_users: enUsers,
        disabled_for_users: disUsers,
      };
      const { data } = await api.patch(`/feature-flags/${flag.key}`, payload);
      toast.success(`Flag "${data.label}" salvato`);
      setDirty(false);
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/feature-flags/${flag.key}`);
      toast.success(`Flag "${flag.label}" eliminato`);
      setConfirmDelOpen(false);
      onDeleted?.(flag.key);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  const usersWithAccess = useMemo(() => {
    // Stima numero utenti che perderanno accesso se disattivo global (per warning)
    if (!flag.enabled_globally) return 0;
    // Se global era on, tutti gli utenti sono potenzialmente attivi meno disabled
    return users.length - (disUsers.length || 0);
  }, [users, disUsers, flag.enabled_globally]);

  return (
    <div className="ih-glass rounded-2xl p-5 space-y-4" data-testid={`flag-card-${flag.key}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <FlagIcon className="h-4 w-4 text-lavender-600" />
            <span className="font-mono text-xs px-2 py-0.5 rounded bg-muted text-muted-foreground">{flag.key}</span>
            <StatusBadge flag={{ ...flag, enabled_globally: eg, enabled_for_roles: roles, enabled_for_users: enUsers }} />
          </div>
          <Input
            data-testid={`flag-label-${flag.key}`}
            value={label}
            onChange={(e) => { setLabel(e.target.value); markDirty(); }}
            className="text-base font-medium bg-background/40 border-transparent hover:border-border focus:border-lavender-300"
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid={`flag-delete-${flag.key}`}
            variant="outline"
            size="sm"
            onClick={() => setConfirmDelOpen(true)}
            className="text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40 border-rose-300/60"
          >
            <Trash2 className="h-3.5 w-3.5 mr-1" /> Elimina
          </Button>
          <Button
            data-testid={`flag-save-${flag.key}`}
            size="sm"
            onClick={doSave}
            disabled={!dirty || saving}
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
            Salva
          </Button>
        </div>
      </div>

      <Textarea
        data-testid={`flag-desc-${flag.key}`}
        value={desc}
        onChange={(e) => { setDesc(e.target.value); markDirty(); }}
        placeholder="Descrizione della feature"
        className="text-sm bg-background/40 min-h-[60px]"
      />

      {/* Global toggle */}
      <div className="flex items-center justify-between p-3 rounded-xl bg-lavender-50/40 dark:bg-lavender-950/20 border border-lavender-200/40 dark:border-lavender-900/30">
        <div>
          <div className="text-sm font-medium">Attivo per tutti</div>
          <div className="text-xs text-muted-foreground">
            Se attivo, la feature è visibile a chiunque (ignorando ruoli/utenti selezionati). Gli utenti nella lista "disabilitati" restano fuori.
          </div>
        </div>
        <Switch
          data-testid={`flag-global-${flag.key}`}
          checked={eg}
          onCheckedChange={attemptToggleGlobal}
        />
      </div>

      {/* Roles */}
      <div className="space-y-1.5">
        <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground flex items-center gap-1.5">
          <Users className="h-3.5 w-3.5" /> Ruoli abilitati
        </div>
        <div className="flex flex-wrap gap-1.5">
          {ALL_ROLES.map((r) => {
            const on = roles.includes(r);
            return (
              <button
                key={r}
                type="button"
                data-testid={`flag-role-${flag.key}-${r}`}
                onClick={() => toggleRole(r)}
                className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${on
                  ? "bg-lavender-500 text-white border-lavender-500"
                  : "border-border hover:bg-accent"
                }`}
              >{ROLE_LABELS[r] || r}</button>
            );
          })}
        </div>
      </div>

      {/* Enabled users */}
      <UserPicker
        testIdPrefix={`flag-en-${flag.key}`}
        label="Utenti abilitati (override positivo)"
        icon={UserCheck}
        users={users}
        selected={enUsers}
        onChange={(v) => { setEnUsers(v); markDirty(); }}
        colorClass="text-emerald-700 dark:text-emerald-300"
      />

      {/* Disabled users */}
      <UserPicker
        testIdPrefix={`flag-dis-${flag.key}`}
        label="Utenti disabilitati (override negativo)"
        icon={UserX}
        users={users}
        selected={disUsers}
        onChange={(v) => { setDisUsers(v); markDirty(); }}
        colorClass="text-rose-700 dark:text-rose-300"
      />

      <div className="text-[11px] text-muted-foreground pt-2 border-t border-border/40">
        Aggiornato: {flag.updated_at ? new Date(flag.updated_at).toLocaleString("it-IT") : "—"}
      </div>

      {/* Confirm delete */}
      <Dialog open={confirmDelOpen} onOpenChange={setConfirmDelOpen}>
        <DialogContent className="ih-glass-strong sm:max-w-md" data-testid={`flag-delete-modal-${flag.key}`}>
          <DialogHeader>
            <DialogTitle className="text-rose-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Eliminare il flag?
            </DialogTitle>
            <DialogDescription>
              Il flag <strong className="text-foreground">{flag.key}</strong> sarà eliminato definitivamente.
              <br />
              Il codice frontend potrebbe non gestire correttamente la sua assenza — la feature risulterà disabilitata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelOpen(false)}>Annulla</Button>
            <Button
              data-testid={`flag-delete-confirm-${flag.key}`}
              onClick={doDelete}
              disabled={deleting}
              className="bg-rose-600 hover:bg-rose-700 text-white"
            >
              {deleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
              Elimina definitivamente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm global off */}
      <Dialog open={confirmGlobalOffOpen} onOpenChange={setConfirmGlobalOffOpen}>
        <DialogContent className="ih-glass-strong sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-amber-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" /> Disattivare "Attivo globale"?
            </DialogTitle>
            <DialogDescription>
              Attualmente ~{usersWithAccess} utenti hanno accesso a questa feature. Disattivando lo stato globale,
              rimarranno visibili solo agli utenti/ruoli esplicitamente elencati.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmGlobalOffOpen(false)}>Annulla</Button>
            <Button
              onClick={() => { setEg(false); setDirty(true); setConfirmGlobalOffOpen(false); }}
              className="bg-amber-600 hover:bg-amber-700 text-white"
            >Conferma</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateFlagModal({ open, onOpenChange, onCreated }) {
  const [key, setKey] = useState("");
  const [label, setLabel] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const keyValid = /^[a-z][a-z0-9_]{1,63}$/.test(key);

  const submit = async () => {
    if (!keyValid || !label.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/feature-flags", {
        key, label, description: desc, enabled_globally: false,
        enabled_for_roles: [], enabled_for_users: [], disabled_for_users: [],
      });
      toast.success(`Flag "${data.label}" creato`);
      setKey(""); setLabel(""); setDesc("");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore creazione flag");
    } finally { setCreating(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="ih-glass-strong sm:max-w-md" data-testid="create-flag-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
            <Plus className="h-5 w-5 text-lavender-500" />
            Nuovo feature flag
          </DialogTitle>
          <DialogDescription>
            La chiave deve essere in snake_case e viene usata nel codice per gate-are la feature.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2">
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Chiave *</label>
            <Input
              data-testid="new-flag-key"
              value={key}
              onChange={(e) => setKey(e.target.value.toLowerCase())}
              placeholder="es. web_reputation, ai_advisor"
              className="font-mono bg-background/40"
            />
            {key && !keyValid && (
              <p className="text-xs text-rose-600">Solo lettere minuscole, numeri e underscore. Deve iniziare con una lettera. Min 2 char.</p>
            )}
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Etichetta *</label>
            <Input
              data-testid="new-flag-label"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="es. Web Reputation avanzata"
              className="bg-background/40"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs uppercase tracking-[0.16em] text-muted-foreground">Descrizione</label>
            <Textarea
              data-testid="new-flag-desc"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="Cosa fa questa feature?"
              className="bg-background/40 min-h-[70px]"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annulla</Button>
          <Button
            data-testid="create-flag-submit"
            onClick={submit}
            disabled={!keyValid || !label.trim() || creating}
          >
            {creating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Plus className="h-4 w-4 mr-1" />}
            Crea flag
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminFeatureFlags() {
  const { refresh: refreshMyFlags } = useFeatureFlags();
  const [flags, setFlags] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [fRes, uRes] = await Promise.all([
        api.get("/feature-flags"),
        api.get("/users"),
      ]);
      setFlags(fRes.data || []);
      setUsers(uRes.data || []);
    } catch {
      toast.error("Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSaved = async (updated) => {
    setFlags((prev) => prev.map((f) => (f.key === updated.key ? updated : f)));
    // Aggiorna i miei flag (per hide/show voce menu subito)
    await refreshMyFlags();
  };
  const onDeleted = async (key) => {
    setFlags((prev) => prev.filter((f) => f.key !== key));
    await refreshMyFlags();
  };
  const onCreated = async (f) => {
    setFlags((prev) => [...prev, f].sort((a, b) => a.key.localeCompare(b.key)));
    await refreshMyFlags();
  };

  return (
    <div className="space-y-6 ih-anim-in" data-testid="admin-feature-flags-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1">Configurazione</div>
          <h1 className="font-display text-3xl tracking-tight font-light flex items-center gap-2">
            <FlagIcon className="h-7 w-7 text-lavender-500" />
            Feature Flags
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            Abilita/disabilita feature per utente o ruolo senza deploy. Le modifiche si propagano a tutti gli utenti entro 60 secondi (o subito al login).
          </p>
        </div>
        <button
          data-testid="open-create-flag"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium bg-lavender-500 hover:bg-lavender-600 text-white shadow-md"
        >
          <Plus className="h-4 w-4" />
          Nuovo flag
        </button>
      </div>

      <div className="ih-glass rounded-xl p-3 text-xs text-amber-800 dark:text-amber-300 bg-amber-50/60 dark:bg-amber-950/30 border-amber-300/40 flex gap-2 items-start">
        <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
        <span>
          <strong>Nota di sicurezza:</strong> nascondere una voce menu <em>non</em> è protezione dei dati.
          I feature flag servono solo per il rendering; ogni endpoint sensibile deve avere il proprio RBAC lato backend.
        </span>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => <Skeleton key={i} className="h-64 rounded-2xl" />)}
        </div>
      ) : flags.length === 0 ? (
        <div className="ih-glass rounded-2xl p-10 text-center text-muted-foreground" data-testid="flags-empty">
          Nessun flag configurato. Crea il primo con <strong>"Nuovo flag"</strong>.
        </div>
      ) : (
        <div className="space-y-4">
          {flags.map((f) => (
            <FlagCard key={f.key} flag={f} users={users} onSaved={onSaved} onDeleted={onDeleted} />
          ))}
        </div>
      )}

      <CreateFlagModal open={createOpen} onOpenChange={setCreateOpen} onCreated={onCreated} />
    </div>
  );
}
